import { useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"

import {
  resetBorrowFlow,
  selectUpdateCollateralStrategy,
  useDappSelector,
} from "#/redux-state"
import { useManageCollateralTransaction } from "#/shared/hooks"
import {
  BorrowCollateralManagementActionUnit,
  BorrowCollateralManagementStrategy,
  MezoModalProps,
} from "#/shared/types"
import { ComponentStepProps, StepperModal } from "#/ui/Modal"

import ManageAction from "./ManageAction"
import ManageReview from "./ManageReview"
import ManageSetup from "./ManageSetup"
import ManageTransaction from "./ManageTransaction"

const ManageCollateralStepTitle = {
  [BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL]: "Deposit",
  [BorrowCollateralManagementStrategy.WITHDRAW_COLLATERAL]: "Withdraw",
}

const ManageCollateralReviewTitle = {
  [BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL]: "Review deposit",
  [BorrowCollateralManagementStrategy.WITHDRAW_COLLATERAL]: "Review withdrawal",
}

export default function ManageCollateralModal({
  onClose,
  ...props
}: MezoModalProps) {
  const dispatch = useDispatch()
  const strategy = useDappSelector(selectUpdateCollateralStrategy)

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

  const [activeSetupAction, setActiveSetupAction] =
    useState<BorrowCollateralManagementActionUnit>(
      BorrowCollateralManagementActionUnit.AMOUNT,
    )

  const { isLoading, hasError, updateCollateralHandler } =
    useManageCollateralTransaction()

  const handleClose = useCallback(() => {
    dispatch(resetBorrowFlow())
    onClose()
  }, [dispatch, onClose])

  const steps = useMemo(
    () => [
      {
        title: "Manage your collateral",
        withCloseButton: true,
        component: (stepProps: ComponentStepProps) => (
          <ManageAction {...stepProps} />
        ),
      },
      {
        title: strategy ? ManageCollateralStepTitle[strategy] : "",
        withCloseButton: true,
        component: (stepProps: ComponentStepProps) => (
          <ManageSetup
            {...stepProps}
            activeSetupAction={activeSetupAction}
            setActiveSetupAction={setActiveSetupAction}
          />
        ),
      },
      {
        title: strategy ? ManageCollateralReviewTitle[strategy] : "",
        withCloseButton: true,
        component: (stepProps: ComponentStepProps) => (
          <ManageReview
            {...stepProps}
            activeSetupAction={activeSetupAction}
            hasAcceptedTerms={hasAcceptedTerms}
            setHasAcceptedTerms={setHasAcceptedTerms}
            handleManageCollateral={updateCollateralHandler}
          />
        ),
      },
      {
        title: "Sign transaction",
        withCloseButton: false,
        component: (stepProps: ComponentStepProps) => (
          <ManageTransaction
            {...stepProps}
            isLoading={isLoading}
            hasError={hasError}
          />
        ),
      },
    ],
    [
      activeSetupAction,
      hasAcceptedTerms,
      hasError,
      isLoading,
      strategy,
      updateCollateralHandler,
    ],
  )

  return (
    <StepperModal
      {...props}
      variant="tall"
      onClose={handleClose}
      steps={steps}
    />
  )
}
